/**
 * Animalia token layer.
 *
 * Token names mirror the real Figma variable tree from the Animalia library
 * (file `2026`, node 137:280) so what renders here matches what a designer
 * sees in the file. Three tiers:
 *
 *   1. primitives  — the shared layout values and five curated color/type palettes
 *   2. component   — `Ani Button/Primary/--color` & friends, aliasing tier 1
 *   3. CSS         — `animalia.css`, which only ever reads schema aliases
 *
 * Everything is emitted as CSS custom properties onto a single scope element,
 * so re-theming the whole playground is one `style` object swap and nothing
 * leaks into the surrounding portfolio page.
 */

// ── colour math ──────────────────────────────────────────────────────────────

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function channelToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return (
    0.2126 * channelToLinear(r) +
    0.7152 * channelToLinear(g) +
    0.0722 * channelToLinear(b)
  );
}

/** WCAG 2.x contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.min(100, Math.max(0, s)) / 100;
  const lig = Math.min(100, Math.max(0, l)) / 100;
  const k = (n: number) => (n + hue / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n: number) =>
    lig - a * Math.max(-1, Math.min(Math.min(k(n) - 3, 9 - k(n)), 1));
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/** Append an 8-bit alpha channel to a hex colour. */
function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
}

// ── theme shape ──────────────────────────────────────────────────────────────

type BrandRamp = Record<"darkest" | "darker" | "dark" | "base" | "light" | "lighter" | "lightest", string>;

/** Resolved primitive values a theme is assembled from. */
type Ingredients = {
  primary: string;
  primaryHover: string;
  primaryPressed: string;
  primaryText: string;
  brand: BrandRamp;
  /** Ani Button/Secondary/--background-hover — also the combobox hover fill. */
  secondaryHover: string;
  /** Ani Button/Secondary/--background-pressed. */
  secondaryPressed: string;
  /** Ani Button/Secondary/--text-color-hover, Ani Button/Tertiary/--color-pressed. */
  onSecondary: string;
  /** Focus ring + combobox hover border. */
  focusOutline: string;
  comboboxBackground: string;
  comboboxColorHover: string;
  disabled: string;
  chart01: string;
  categorical: string[];
  neutral00: string;
  surface: string;
  neutralMid40: string;
  borderColor: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  shadowColor: string;
  radiusMd: number;
  radiusDefault: number;
  radiusButton: number;
  radiusLg: number;
  radiusPill: number;
  borderSm: number;
  borderMd: number;
  spaceUnit: number;
  familyUi: string;
  familyHeading: string;
};

export type AniTheme = {
  /** Shown in the X-Ray legend header. */
  label: string;
  fontLabel: string;
  /** Contrast of body text on the card surface — surfaced in X-Ray. */
  textContrast: number;
  /** Contrast of the primary button's label on its own fill. */
  buttonContrast: number;
  vars: Record<string, string>;
};

/** Numeric values needed by non-CSS renderers (Next Image and ECharts). */
export const ANIMALIA_TOKEN_VALUES = {
  avatarSize: 32,
  mascotSize: 72,
  xrayTooltipMaxWidth: 280,
} as const;

/**
 * Schema layer. Every component token in the Figma library is aliased onto
 * the semantic and primitive values above. The keys are the CSS custom
 * properties `animalia.css` consumes; `TOKEN_REGISTRY` maps them back to
 * their Figma paths.
 */
function buildVars(i: Ingredients): Record<string, string> {
  const px = (n: number) => `${n}px`;
  const u = i.spaceUnit;

  return {
    // primitives ────────────────────────────────────────────────
    // Canonical names mirror the Foundation Global collection. Short aliases
    // below keep the component styles readable while preserving the chain.
    ...Object.fromEntries(Object.entries(i.brand).map(([shade, value]) => [`--ani-color-brand-01-${shade}`, value])),
    "--ani-color-neutral-light-00": i.neutral00,
    "--ani-color-neutral-light-10": i.surface,
    "--ani-color-neutral-mid-40": i.neutralMid40,
    "--ani-color-action-default": i.primary,
    "--ani-color-action-hover": i.primaryHover,
    "--ani-color-action-pressed": i.primaryPressed,
    "--ani-color-content-default": i.text,
    "--ani-color-content-secondary": i.textSecondary,
    "--ani-color-content-muted": i.textMuted,
    "--ani-color-surface-default": "var(--ani-color-neutral-light-10)",
    "--ani-color-surface-raised": "var(--ani-color-neutral-light-00)",
    "--ani-color-border-default": i.borderColor,
    "--ani-color-action-focus": i.focusOutline,
    "--ani-color-action-disabled": i.disabled,
    "--ani-border-radius-default": px(i.radiusDefault),
    "--ani-border-radius-sm": px(i.radiusMd),
    "--ani-border-radius-lg": px(i.radiusLg),
    "--ani-border-radius-pill": px(i.radiusPill),
    "--ani-border-radius-button": px(i.radiusButton),
    "--ani-primary": "var(--ani-color-action-default)",
    "--ani-brand-01-base": "var(--ani-color-brand-01-base)",
    "--ani-neutral-00": "var(--ani-color-neutral-light-00)",
    "--ani-neutral-mid-40": "var(--ani-color-neutral-mid-40)",
    "--ani-surface": "var(--ani-color-neutral-light-10)",
    "--ani-text": "var(--ani-color-content-default)",
    "--ani-text-secondary": "var(--ani-color-content-secondary)",
    "--ani-text-muted": "var(--ani-color-content-muted)",
    "--ani-border-color": i.borderColor,
    "--ani-shadow-color": i.shadowColor,

    "--ani-space-none": "0px",
    "--ani-spacing-xxs": px(Math.round(u / 2)),
    "--ani-spacing-xs": px(u),
    "--ani-spacing-sm": px(u * 2),
    "--ani-spacing-md": px(u * 3),
    "--ani-space-xxs": "var(--ani-spacing-xxs)",
    "--ani-space-xs": "var(--ani-spacing-xs)",
    "--ani-space-sm": "var(--ani-spacing-sm)",
    "--ani-space-md": "var(--ani-spacing-md)",
    "--ani-space-lg": px(u * 6),
    "--ani-layout-stage-padding-y-min": "32px",
    "--ani-layout-stage-padding-y-max": "88px",
    "--ani-layout-stage-padding-x-min": "16px",
    "--ani-layout-stage-padding-x-max": "64px",
    "--ani-layout-stage-max-width": "2052px",
    "--ani-layout-rail-padding-left": "200px",
    "--ani-layout-rail-padding-right-min": "40px",
    "--ani-layout-rail-padding-right-max": "120px",
    "--ani-layout-controls-margin-bottom-min": "24px",
    "--ani-layout-controls-margin-bottom-max": "40px",
    "--ani-layout-grid-gap-min": "24px",
    "--ani-layout-grid-gap-max": "148px",
    "--ani-calendar-year-width": "88px",
    "--ani-calendar-nav-width": "64px",
    "--ani-combobox-min-width": "72px",
    "--ani-combobox-wide-width": "116px",
    "--ani-control-min-width": "127px",
    "--ani-search-avatar-size": `${ANIMALIA_TOKEN_VALUES.mascotSize}px`,
    "--ani-search-card-padding-y": "var(--ani-space-xs)",
    // Room for all three mascot cards, so an answer never moves the field.
    "--ani-search-results-min-height":
      "calc(3 * (var(--ani-search-avatar-size) + 2 * var(--ani-search-card-padding-y) + 2 * var(--ani-border-sm)) + 2 * var(--ani-space-xs))",
    "--ani-modal-max-width": "568px",
    "--ani-chart-title-letter-spacing": "0.24px",
    "--ani-chart-menu-min-width": "168px",
    "--ani-xray-title-gap": "10px",
    "--ani-xray-row-height": "44px",
    "--ani-xray-button-height": "32px",
    "--ani-popover-layer": "10",
    "--ani-chart-grid-top": "12px",
    "--ani-chart-grid-right": "8px",
    "--ani-chart-grid-bottom": "78px",
    "--ani-chart-grid-left": "36px",
    "--ani-chart-legend-bottom": "4px",
    "--ani-chart-legend-width": "10px",
    "--ani-chart-legend-height": "100px",
    "--ani-chart-cell-border-width": "2px",
    "--ani-tooltip-max-width": `${ANIMALIA_TOKEN_VALUES.xrayTooltipMaxWidth}px`,
    "--ani-tooltip-max-height": "240px",
    "--ani-size-xs": "24px",
    "--ani-font-2xs": "10px",
    "--ani-font-compact": "11px",
    "--ani-font-lg": "24px",
    "--ani-line-height-tight": "1",
    "--ani-line-height-default": "1.5",
    "--ani-line-height-caption": "1.4",
    "--ani-line-height-tooltip": "1.6",
    "--ani-radius-minimal": "var(--ani-border-radius-sm)",
    "--ani-focus-offset": "2px",
    "--ani-underline-offset": "3px",
    "--ani-avatar-overlap": "8px",
    "--ani-avatar-hover-shift": "2px",
    "--ani-modal-shadow": "0 6px 12px #0000001a, 0 0 3px #0000001a",
    "--ani-modal-backdrop": "#06131d99",
    "--ani-modal-surface-opacity": "92%",
    "--ani-modal-backdrop-blur": "3px",
    "--ani-card-padding": "var(--ani-space-sm)",
    "--ani-card-max-width": "408px",
    "--ani-calendar-min-height": "377px",
    "--ani-task-card-min-height": "309px",
    "--ani-chart-card-min-height": "377px",
    "--ani-chart-height": "285px",
    "--ani-playground-row-gap": "44px",
    "--ani-playground-controls-gap": "48px",

    "--ani-radius-default": "var(--ani-border-radius-default)",
    "--ani-radius-lg": "var(--ani-border-radius-lg)",
    "--ani-radius-pill": "var(--ani-border-radius-pill)",

    "--ani-border-width-sm": px(i.borderSm),
    "--ani-border-width-md": px(i.borderMd),
    "--ani-border-sm": "var(--ani-border-width-sm)",
    "--ani-border-md": "var(--ani-border-width-md)",

    // Focus indicator — a 2px ring held one focus-size away from the control,
    // which is why the design carries a second, larger radius for it.
    "--ani-focus-size": "2px",
    "--ani-focus-outline-color": i.focusOutline,
    "--ani-radius-focus": px(i.radiusDefault + 2),

    // Control heights are fixed tokens in the library, not derived from the
    // spacing unit — Target/Target Size is the accessibility floor.
    "--ani-size-sm": `${ANIMALIA_TOKEN_VALUES.avatarSize}px`,
    "--ani-size-md": "44px",
    "--ani-size-lg": "56px",
    "--ani-icon-sm": "16px",
    "--ani-icon-md": "24px",

    "--ani-font-xs": "12px",
    "--ani-font-sm": "14px",
    "--ani-font-default": "16px",
    "--ani-weight-normal": "400",
    "--ani-weight-semibold": "600",
    "--ani-weight-bold": "700",
    "--ani-family-ui": i.familyUi,
    "--ani-family-heading": i.familyHeading,

    "--ani-blur": "16px",
    "--ani-target-target-size": `${ANIMALIA_TOKEN_VALUES.avatarSize}px`,
    "--ani-target-size": "var(--ani-target-target-size)",

    // Ani Button ────────────────────────────────────────────────
    "--ani-button-border-radius": "var(--ani-border-radius-button)",
    "--ani-button-border-radius-focus": px(i.radiusButton + 2),
    "--ani-button-primary-color": "var(--ani-color-action-default)",
    "--ani-button-primary-color-hover": "var(--ani-color-action-hover)",
    "--ani-button-primary-color-pressed": "var(--ani-color-action-pressed)",
    "--ani-button-primary-text-color": i.primaryText,
    "--ani-button-primary-color-disabled": i.disabled,
    "--ani-button-primary-text-color-disabled": i.textSecondary,
    "--ani-button-primary-outline-color-focus": i.focusOutline,
    "--ani-button-secondary-background-hover": i.secondaryHover,
    "--ani-button-secondary-background-pressed": i.secondaryPressed,
    "--ani-button-secondary-text-color-hover": i.onSecondary,
    "--ani-button-tertiary-color-pressed": i.onSecondary,

    // Ani Card ──────────────────────────────────────────────────
    "--ani-card-border-radius": "var(--ani-radius-lg)",
    "--ani-card-readonly-background": withAlpha(i.neutral00, 0.7),
    "--ani-card-readonly-border-color": i.borderColor,

    // Ani Calendar ──────────────────────────────────────────────
    "--ani-calendar-border-radius": "var(--ani-radius-default)",
    "--ani-calendar-base-background": i.neutral00,
    "--ani-calendar-base-border-color": i.borderColor,
    "--ani-calendar-weekly-text-color": i.text,
    "--ani-calendar-day-text-color": i.text,
    "--ani-calendar-day-next-text-color": i.textMuted,
    "--ani-calendar-day-today-text-color": i.primary,

    // Ani Combobox ──────────────────────────────────────────────
    "--ani-combobox-background": i.comboboxBackground,
    "--ani-combobox-color": i.textSecondary,
    "--ani-combobox-text-color": i.text,
    "--ani-combobox-text-color-placeholder": i.textMuted,
    "--ani-combobox-background-hover": i.secondaryHover,
    "--ani-combobox-color-hover": i.comboboxColorHover,
    "--ani-combobox-color-focus": i.primary,
    "--ani-combobox-outline-color-focus": i.focusOutline,
    "--ani-combobox-background-disabled": i.borderColor,
    "--ani-combobox-color-disabled": i.textMuted,

    // Ani Text Field ────────────────────────────────────────────
    "--ani-text-field-border-radius": "var(--ani-radius-default)",
    "--ani-text-field-border-radius-focus": "var(--ani-radius-focus)",

    // Ani Checkbox ──────────────────────────────────────────────
    "--ani-checkbox-background": i.neutral00,
    "--ani-checkbox-background-selected": i.primary,
    "--ani-checkbox-border-color": i.textSecondary,

    // Ani Tag ───────────────────────────────────────────────────
    "--ani-tag-status-color-neutral": "var(--ani-color-neutral-light-10)",
    "--ani-tag-status-text-color-neutral": "var(--ani-color-content-secondary)",

    // Ani Tag / Removable states from node 519:2762.
    "--ani-tag-removable-color": i.secondaryHover,
    "--ani-tag-removable-border-color": i.secondaryPressed,
    "--ani-tag-removable-text-color": i.textSecondary,
    "--ani-tag-removable-icon-color": i.primary,
    "--ani-tag-removable-color-hover": i.secondaryPressed,
    "--ani-tag-removable-outline-focus": i.focusOutline,
    "--ani-tag-removable-color-disabled": i.borderColor,
    "--ani-tag-removable-border-disabled": i.disabled,
    "--ani-tag-removable-text-disabled": i.textSecondary,
    "--ani-tag-removable-icon-disabled": i.disabled,

    // Ani Chart ─────────────────────────────────────────────────
    "--ani-chart-header-title": i.textSecondary,
    "--ani-chart-grid": i.borderColor,
    "--ani-chart-categorical-01": i.chart01,
    "--ani-chart-footer-caption": i.textSecondary,
    "--ani-chart-footer-color-01": i.categorical[0],
    "--ani-chart-footer-color-02": i.categorical[1],
    "--ani-chart-footer-color-03": i.categorical[2],
    "--ani-chart-footer-color-04": i.categorical[3],
    "--ani-chart-footer-color-05": i.categorical[4],
    "--ani-chart-footer-color-06": i.categorical[5],

    // Ani Search AI ─────────────────────────────────────────────
    "--ani-search-background": i.neutral00,
    "--ani-search-border-color": i.textSecondary,
    "--ani-search-leading-icon-color": i.textSecondary,
    "--ani-search-placeholder-color": i.textMuted,
    "--ani-search-action-icon-color": i.primary,
    "--ani-search-processing-color": i.primary,
    "--ani-search-processing-background": "var(--ani-color-brand-01-lightest)",
    "--ani-search-processing-shimmer": "var(--ani-color-brand-01-light)",

    // odds and ends ─────────────────────────────────────────────
    "--ani-icon-color": i.text,
    "--ani-avatar-outline-color": i.surface,
    "--ani-divider-color": i.neutralMid40,
    "--ani-label-text-color": i.text,
    "--ani-shadow-sm": i.shadowColor === "#00000000" ? "none" : `0 0 3px 0 ${i.shadowColor}, 0 4px 8px 0 ${i.shadowColor}`,
  };
}

// ── Animalia foundation ──────────────────────────────────────────────────────

/** Exact brand/01 primitives supplied from the Animalia library. */
const ANIMALIA_BRAND: BrandRamp = {
  darkest: "#030330", darker: "#0D0D59", dark: "#1F1F7A", base: "#4545A1",
  light: "#9898CD", lighter: "#C2C2E5", lightest: "#EBEBF5",
};

/** The supplied brand ramp and Inter, with a separate grayscale neutral scale. */
const ANIMALIA_INGREDIENTS: Ingredients = {
  primary: ANIMALIA_BRAND.base,
  primaryHover: ANIMALIA_BRAND.dark,
  primaryPressed: ANIMALIA_BRAND.darker,
  primaryText: "#FFFFFF",
  brand: ANIMALIA_BRAND,
  secondaryHover: ANIMALIA_BRAND.lightest,
  secondaryPressed: ANIMALIA_BRAND.lighter,
  onSecondary: ANIMALIA_BRAND.darkest,
  focusOutline: ANIMALIA_BRAND.dark,
  comboboxBackground: "#FAFAFA",
  comboboxColorHover: ANIMALIA_BRAND.dark,
  disabled: "#D4D4D4",
  chart01: ANIMALIA_BRAND.base,
  categorical: [ANIMALIA_BRAND.base, "#C25534", "#792CC7", "#008768", "#D62793", "#D44317"],
  neutral00: "#FFFFFF", surface: "#F0F0F0", neutralMid40: "#C4C4C4", borderColor: "#DADADA",
  text: "#171717", textSecondary: "#454545", textMuted: "#707070", shadowColor: "#17171714",
  radiusMd: 4, radiusDefault: 8, radiusButton: 8, radiusLg: 16, radiusPill: 400,
  borderSm: 1, borderMd: 2, spaceUnit: 8,
  familyUi: "var(--font-inter), system-ui, sans-serif",
  familyHeading: "var(--font-inter), system-ui, sans-serif",
};

function themeFromIngredients(label: string, fontLabel: string, ingredients: Ingredients): AniTheme {
  return {
    label,
    fontLabel,
    textContrast: contrastRatio(ingredients.text, ingredients.neutral00),
    buttonContrast: contrastRatio(ingredients.primaryText, ingredients.primary),
    vars: buildVars(ingredients),
  };
}

/** The baseline and four intentionally authored palettes share one layout contract. */
export function animaliaTheme(): AniTheme {
  return themeFromIngredients("Animalia", "Inter", ANIMALIA_INGREDIENTS);
}

type ThemePalette = Pick<Ingredients,
  "primary" | "primaryHover" | "primaryPressed" | "primaryText" | "brand" | "secondaryHover" | "secondaryPressed" |
  "onSecondary" | "focusOutline" | "comboboxBackground" | "comboboxColorHover" |
  "disabled" | "chart01" | "categorical" | "neutral00" | "surface" |
  "neutralMid40" | "borderColor" | "text" | "textSecondary" | "textMuted" | "shadowColor" | "radiusMd" | "radiusDefault" | "radiusButton" | "radiusLg"
>;

function curatedTheme(label: string, fontLabel: string, font: string, palette: ThemePalette): AniTheme {
  return themeFromIngredients(label, fontLabel, {
    ...ANIMALIA_INGREDIENTS,
    ...palette,
    familyUi: font,
    familyHeading: font,
  });
}

export const CURATED_THEMES: readonly AniTheme[] = [
  animaliaTheme(),
  curatedTheme("Airbnb", "Neue Regrade", "var(--font-neue-regrade-variable), system-ui, sans-serif", {
    radiusMd: 6, radiusDefault: 12, radiusButton: 12, radiusLg: 24,
    primary: "#D90845", primaryHover: "#B8073B", primaryPressed: "#92062F", primaryText: "#FFFFFF",
    brand: { darkest: "#460316", darker: "#92062F", dark: "#B8073B", base: "#FF385C", light: "#FF8FA4", lighter: "#FFD1DA", lightest: "#FFF0F3" },
    secondaryHover: "#FFF0F3", secondaryPressed: "#FFD1DA", onSecondary: "#460316",
    focusOutline: "#B8073B", comboboxBackground: "#FFFFFF", comboboxColorHover: "#D90845",
    disabled: "#DDDDDD", chart01: "#D90845",
    categorical: ["#D90845", "#8B531D", "#6654A2", "#26746B", "#376FB0", "#A04572"],
    neutral00: "#FFFFFF", surface: "#F7F7F7", neutralMid40: "#C7C7C7", borderColor: "#DDDDDD",
    text: "#222222", textSecondary: "#484848", textMuted: "#717171", shadowColor: "#22222214",
  }),
  curatedTheme("EE", "Neue Regrade", "var(--font-neue-regrade-variable), system-ui, sans-serif", {
    radiusMd: 6, radiusDefault: 12, radiusButton: 400, radiusLg: 24,
    primary: "#006B70", primaryHover: "#00565A", primaryPressed: "#004145", primaryText: "#FFE600",
    brand: { darkest: "#00272A", darker: "#004145", dark: "#00565A", base: "#006B70", light: "#65B5B8", lighter: "#BFE5E6", lightest: "#EAF7F7" },
    secondaryHover: "#EAF7F7", secondaryPressed: "#BFE5E6", onSecondary: "#00272A",
    focusOutline: "#00565A", comboboxBackground: "#FFFFFF", comboboxColorHover: "#006B70",
    disabled: "#D6D6D6", chart01: "#006B70",
    categorical: ["#006B70", "#8A7300", "#7755A3", "#AE4C64", "#3271A4", "#A25E2A"],
    neutral00: "#FFFFFF", surface: "#F4F4F4", neutralMid40: "#C6C6C6", borderColor: "#D8D8D8",
    text: "#202020", textSecondary: "#494949", textMuted: "#707070", shadowColor: "#20202014",
  }),
  curatedTheme("OpenAI", "System UI", "system-ui, -apple-system, sans-serif", {
    radiusMd: 4, radiusDefault: 8, radiusButton: 8, radiusLg: 16,
    primary: "#171717", primaryHover: "#303030", primaryPressed: "#000000", primaryText: "#FFFFFF",
    brand: { darkest: "#000000", darker: "#0D0D0D", dark: "#121212", base: "#171717", light: "#737373", lighter: "#D4D4D4", lightest: "#F5F5F5" },
    secondaryHover: "#F0F0F0", secondaryPressed: "#E0E0E0", onSecondary: "#171717",
    focusOutline: "#171717", comboboxBackground: "#FFFFFF", comboboxColorHover: "#171717",
    disabled: "#D4D4D4", chart01: "#303030",
    categorical: ["#303030", "#484848", "#606060", "#787878", "#909090", "#A8A8A8"],
    neutral00: "#FFFFFF", surface: "#F5F5F5", neutralMid40: "#CCCCCC", borderColor: "#E0E0E0",
    text: "#171717", textSecondary: "#525252", textMuted: "#707070", shadowColor: "#00000008",
  }),
  curatedTheme("Wireframe", "JetBrains Mono", "var(--font-jetbrains-mono), ui-monospace, monospace", {
    radiusMd: 0, radiusDefault: 0, radiusButton: 0, radiusLg: 0,
    primary: "#333333", primaryHover: "#222222", primaryPressed: "#111111", primaryText: "#FFFFFF",
    brand: { darkest: "#000000", darker: "#111111", dark: "#222222", base: "#333333", light: "#888888", lighter: "#CCCCCC", lightest: "#EEEEEE" },
    secondaryHover: "#EEEEEE", secondaryPressed: "#D6D6D6", onSecondary: "#222222",
    focusOutline: "#333333", comboboxBackground: "#FFFFFF", comboboxColorHover: "#333333",
    disabled: "#D6D6D6", chart01: "#444444",
    categorical: ["#333333", "#4A4A4A", "#616161", "#787878", "#8F8F8F", "#A6A6A6"],
    neutral00: "#FFFFFF", surface: "#EEEEEE", neutralMid40: "#BBBBBB", borderColor: "#B0B0B0",
    text: "#222222", textSecondary: "#4D4D4D", textMuted: "#666666", shadowColor: "#00000000",
  }),
];

// ── X-Ray registry ───────────────────────────────────────────────────────────

export type TokenEntry = {
  /** CSS custom property the components actually read. */
  cssVar: string;
  /** Path as it appears in the Figma variable tree. */
  figma: string;
  kind: "color" | "size" | "font" | "shadow";
};

/**
 * Drives the X-Ray legend. Grouped the way the Figma library is grouped so a
 * designer reading the page recognises their own file.
 */
export const TOKEN_REGISTRY: Array<{ group: string; tokens: TokenEntry[] }> = [
  {
    group: "Primitives",
    tokens: [
      {
        cssVar: "--ani-color-action-default",
        figma: "Ani Button/Primary/--color",
        kind: "color",
      },
      ...(["darkest", "darker", "dark", "base", "light", "lighter", "lightest"] as const).map(shade => ({
        cssVar: `--ani-color-brand-01-${shade}`, figma: `brand/01/${shade}`, kind: "color" as const,
      })),
      { cssVar: "--ani-color-action-hover", figma: "color/action/hover", kind: "color" },
      { cssVar: "--ani-color-action-pressed", figma: "color/action/pressed", kind: "color" },
      { cssVar: "--ani-border-radius-button", figma: "border/radius/button", kind: "size" },
      {
        cssVar: "--ani-color-neutral-light-00",
        figma: "color/neutral/light/00/alpha/100",
        kind: "color",
      },
      {
        cssVar: "--ani-color-neutral-mid-40",
        figma: "color/neutral/mid/40",
        kind: "color",
      },
      { cssVar: "--ani-spacing-xxs", figma: "spacing/xxs", kind: "size" },
      { cssVar: "--ani-spacing-xs", figma: "spacing/xs", kind: "size" },
      { cssVar: "--ani-spacing-sm", figma: "spacing/sm", kind: "size" },
      { cssVar: "--ani-spacing-md", figma: "spacing/md", kind: "size" },
      { cssVar: "--ani-border-radius-sm", figma: "border-radius/sm", kind: "size" },
      {
        cssVar: "--ani-border-radius-default",
        figma: "border/radius/default",
        kind: "size",
      },
      {
        cssVar: "--ani-border-radius-pill",
        figma: "border-radius/pill",
        kind: "size",
      },
      { cssVar: "--ani-border-width-sm", figma: "border-width/sm", kind: "size" },
      { cssVar: "--ani-border-width-md", figma: "border-width/md", kind: "size" },
      {
        cssVar: "--ani-focus-size",
        figma: "Focus Indicator/Focus Size",
        kind: "size",
      },
      {
        cssVar: "--ani-focus-outline-color",
        figma: "Ani Button/Primary/--outline-color-focus",
        kind: "color",
      },
      {
        cssVar: "--ani-family-ui",
        figma: "Ani Typography/Family/--ui",
        kind: "font",
      },
      {
        cssVar: "--ani-target-target-size",
        figma: "Target/Target Size",
        kind: "size",
      },
    ],
  },
  {
    group: "Layout & Rendering",
    tokens: [
      { cssVar: "--ani-layout-stage-max-width", figma: "Container-X-Ray/--max-width", kind: "size" },
      { cssVar: "--ani-layout-stage-padding-y-min", figma: "Container-X-Ray/--padding-y-min", kind: "size" },
      { cssVar: "--ani-layout-stage-padding-y-max", figma: "Container-X-Ray/--padding-y-max", kind: "size" },
      { cssVar: "--ani-layout-stage-padding-x-min", figma: "Container-X-Ray/--padding-x-min", kind: "size" },
      { cssVar: "--ani-layout-stage-padding-x-max", figma: "Container-X-Ray/--padding-x-max", kind: "size" },
      { cssVar: "--ani-layout-grid-gap-min", figma: "Container-X-Ray/--grid-gap-min", kind: "size" },
      { cssVar: "--ani-layout-grid-gap-max", figma: "Container-X-Ray/--grid-gap-max", kind: "size" },
      { cssVar: "--ani-calendar-year-width", figma: "Ani Calendar/Year/--width", kind: "size" },
      { cssVar: "--ani-calendar-nav-width", figma: "Ani Calendar/Navigation/--width", kind: "size" },
      { cssVar: "--ani-combobox-min-width", figma: "Ani Combobox/--min-width", kind: "size" },
      { cssVar: "--ani-combobox-wide-width", figma: "Ani Combobox/Wide/--width", kind: "size" },
      { cssVar: "--ani-control-min-width", figma: "Target/Control/--min-width", kind: "size" },
      { cssVar: "--ani-search-results-min-height", figma: "Ani Search AI/Results/--min-height", kind: "size" },
      { cssVar: "--ani-search-avatar-size", figma: "Ani Search AI/Results/Avatar/--size", kind: "size" },
      { cssVar: "--ani-search-card-padding-y", figma: "Ani Search AI/Results/Card/--padding-y", kind: "size" },
      { cssVar: "--ani-modal-max-width", figma: "Ani Chart/Modal/--max-width", kind: "size" },
      { cssVar: "--ani-chart-title-letter-spacing", figma: "Ani Chart/Header/Title/--letter-spacing", kind: "size" },
      { cssVar: "--ani-chart-menu-min-width", figma: "Ani Chart/Header/Menu/--min-width", kind: "size" },
      { cssVar: "--ani-popover-layer", figma: "Layer/Popover", kind: "size" },
      { cssVar: "--ani-modal-shadow", figma: "Ani Shadow/Modal/--shadow", kind: "shadow" },
      { cssVar: "--ani-modal-backdrop", figma: "Ani Chart/Modal/--backdrop", kind: "color" },
      { cssVar: "--ani-size-xs", figma: "Target/Size/XS", kind: "size" },
      { cssVar: "--ani-font-2xs", figma: "Ani Typography/Size/2XS", kind: "font" },
      { cssVar: "--ani-font-compact", figma: "Ani Typography/Size/Compact", kind: "font" },
      { cssVar: "--ani-font-lg", figma: "Ani Typography/Size/LG", kind: "font" },
      { cssVar: "--ani-line-height-default", figma: "Ani Typography/Line Height/Default", kind: "size" },
      { cssVar: "--ani-line-height-caption", figma: "Ani Typography/Line Height/Caption", kind: "size" },
      { cssVar: "--ani-radius-minimal", figma: "border/radius/minimal", kind: "size" },
      { cssVar: "--ani-focus-offset", figma: "Focus Indicator/Offset", kind: "size" },
      { cssVar: "--ani-underline-offset", figma: "Ani Typography/Underline/Offset", kind: "size" },
      { cssVar: "--ani-avatar-overlap", figma: "Ani Avatar/Group/--overlap", kind: "size" },
      { cssVar: "--ani-avatar-hover-shift", figma: "Ani Avatar/Group/--hover-shift", kind: "size" },
    ],
  },
  {
    group: "Button",
    tokens: [
      { cssVar: "--ani-button-primary-color-hover", figma: "Ani Button/Primary/--color-hover", kind: "color" },
      { cssVar: "--ani-button-primary-color-pressed", figma: "Ani Button/Primary/--color-pressed", kind: "color" },
      { cssVar: "--ani-button-primary-text-color-disabled", figma: "Ani Button/Primary/--text-color-disabled", kind: "color" },
      {
        cssVar: "--ani-button-primary-color",
        figma: "Ani Button/Primary/--color",
        kind: "color",
      },
      {
        cssVar: "--ani-button-primary-text-color",
        figma: "Ani Button/Primary/--text-color",
        kind: "color",
      },
      {
        cssVar: "--ani-button-border-radius",
        figma: "Ani Button/--border-radius",
        kind: "size",
      },
      {
        cssVar: "--ani-button-border-radius-focus",
        figma: "Ani Button/--border-radius-focus",
        kind: "size",
      },
      {
        cssVar: "--ani-button-primary-color-disabled",
        figma: "Ani Button/Primary/--color-disabled",
        kind: "color",
      },
      {
        cssVar: "--ani-button-secondary-background-hover",
        figma: "Ani Button/Secondary/--background-hover",
        kind: "color",
      },
      {
        cssVar: "--ani-button-secondary-background-pressed",
        figma: "Ani Button/Secondary/--background-pressed",
        kind: "color",
      },
      {
        cssVar: "--ani-button-tertiary-color-pressed",
        figma: "Ani Button/Tertiary/--color-pressed",
        kind: "color",
      },
    ],
  },
  {
    group: "Card",
    tokens: [
      {
        cssVar: "--ani-card-readonly-background",
        figma: "Ani Card/Read-only/--background",
        kind: "color",
      },
      {
        cssVar: "--ani-card-readonly-border-color",
        figma: "Ani Card/Read-only/--border-color",
        kind: "color",
      },
      {
        cssVar: "--ani-card-border-radius",
        figma: "Ani Card/--border-radius",
        kind: "size",
      },
    ],
  },
  {
    group: "Calendar",
    tokens: [
      {
        cssVar: "--ani-calendar-base-background",
        figma: "Ani Calendar/New/Base/--background",
        kind: "color",
      },
      {
        cssVar: "--ani-calendar-base-border-color",
        figma: "Ani Calendar/New/Base/--border-color",
        kind: "color",
      },
      {
        cssVar: "--ani-calendar-day-text-color",
        figma: "Ani Calendar/New/Days Cells/Normal/--text-color",
        kind: "color",
      },
      {
        cssVar: "--ani-calendar-day-next-text-color",
        figma: "Ani Calendar/New/Days Cells/Next Month/--text-color-next",
        kind: "color",
      },
      {
        cssVar: "--ani-calendar-day-today-text-color",
        figma: "Ani Calendar/New/Days Cells/Today/--text-color-today",
        kind: "color",
      },
      {
        cssVar: "--ani-calendar-weekly-text-color",
        figma: "Ani Calendar/New/Weekly Cells/--text-color-weekly",
        kind: "color",
      },
    ],
  },
  {
    group: "Checkbox & Tag",
    tokens: [
      {
        cssVar: "--ani-checkbox-background",
        figma: "Ani Checkbox/--background",
        kind: "color",
      },
      {
        cssVar: "--ani-checkbox-background-selected",
        figma: "Ani Checkbox/--background-selected",
        kind: "color",
      },
      {
        cssVar: "--ani-checkbox-border-color",
        figma: "Ani Checkbox/--border-color",
        kind: "color",
      },
      {
        cssVar: "--ani-tag-status-color-neutral",
        figma: "Ani Tag/Status/--color-neutral",
        kind: "color",
      },
      {
        cssVar: "--ani-tag-status-text-color-neutral",
        figma: "Ani Tag/Status/--text-color-neutral",
        kind: "color",
      },
    ],
  },
  {
    group: "Chart",
    tokens: [
      {
        cssVar: "--ani-chart-header-title",
        figma: "Ani Chart/Header/--title",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-grid",
        figma: "Ani Chart/Content/--grid-chart",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-categorical-01",
        figma: "Ani Chart/Content/--color-categorical-01",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-footer-color-01",
        figma: "Ani Chart/Footer/--color-01",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-footer-color-02",
        figma: "Ani Chart/Footer/--color-02",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-footer-color-03",
        figma: "Ani Chart/Footer/--color-03",
        kind: "color",
      },
      {
        cssVar: "--ani-chart-footer-color-04",
        figma: "Ani Chart/Footer/--color-04",
        kind: "color",
      },
    ],
  },
  {
    group: "Search & Combobox",
    tokens: [
      {
        cssVar: "--ani-search-background",
        figma: "Ani Search AI/Trigger/--background",
        kind: "color",
      },
      {
        cssVar: "--ani-search-border-color",
        figma: "Ani Search AI/Trigger/--border-color",
        kind: "color",
      },
      {
        cssVar: "--ani-search-placeholder-color",
        figma: "Ani Search AI/Trigger/--text-color-placeholder",
        kind: "color",
      },
      {
        cssVar: "--ani-search-action-icon-color",
        figma: "Ani Search AI/Trigger/Action/--icon-color",
        kind: "color",
      },
      {
        cssVar: "--ani-search-processing-color",
        figma: "Ani Search AI/Trigger/--color-icon-processing",
        kind: "color",
      },
      {
        cssVar: "--ani-search-processing-background",
        figma: "Ani Search AI/Trigger/--background-processing",
        kind: "color",
      },
      {
        cssVar: "--ani-search-processing-shimmer",
        figma: "Ani Search AI/Trigger/--shimmer-processing",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-background",
        figma: "Ani Combobox/--background",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-text-color",
        figma: "Ani Combobox/--text-color",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-color",
        figma: "Ani Combobox/--color",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-background-hover",
        figma: "Ani Combobox/--background-hover",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-color-hover",
        figma: "Ani Combobox/--color-hover",
        kind: "color",
      },
      {
        cssVar: "--ani-combobox-color-focus",
        figma: "Ani Combobox/--color-focus",
        kind: "color",
      },
      {
        cssVar: "--ani-text-field-border-radius",
        figma: "Ani Text Field/--border-radius",
        kind: "size",
      },
      {
        cssVar: "--ani-text-field-border-radius-focus",
        figma: "Ani Text Field/--border-radius-focus",
        kind: "size",
      },
    ],
  },
  {
    group: "Avatar, Divider & Shadow",
    tokens: [
      {
        cssVar: "--ani-avatar-outline-color",
        figma: "Ani Avatar/--outline-color",
        kind: "color",
      },
      {
        cssVar: "--ani-divider-color",
        figma: "Ani Divider/--color",
        kind: "color",
      },
      {
        cssVar: "--ani-label-text-color",
        figma: "Ani Label/--text-color",
        kind: "color",
      },
      {
        cssVar: "--ani-shadow-color",
        figma: "Ani Shadow/--color",
        kind: "shadow",
      },
    ],
  },
];

/**
 * The three collection contract used by the Animalia Figma files.  Values are
 * aliases rather than copies so a theme swap still resolves through the same
 * primitive → semantic → schema composition at runtime.
 */
export const TOKEN_LAYERS = {
  primitive: {
    ...Object.fromEntries(Object.keys(ANIMALIA_BRAND).map(shade => [`color/brand/01/${shade}`, `--ani-color-brand-01-${shade}`])),
    "color/neutral/light/00": "--ani-color-neutral-light-00",
    "color/neutral/light/10": "--ani-color-neutral-light-10",
    "color/neutral/mid/40": "--ani-color-neutral-mid-40",
    "color/action/default": "--ani-color-action-default",
    "color/action/hover": "--ani-color-action-hover",
    "color/action/pressed": "--ani-color-action-pressed",
    "spacing/xxs": "--ani-spacing-xxs",
    "spacing/xs": "--ani-spacing-xs",
    "spacing/sm": "--ani-spacing-sm",
    "spacing/md": "--ani-spacing-md",
    "border/radius/default": "--ani-border-radius-default",
    "border/radius/sm": "--ani-border-radius-sm",
    "border/radius/lg": "--ani-border-radius-lg",
    "border/radius/pill": "--ani-border-radius-pill",
    "border/radius/button": "--ani-border-radius-button",
    "border/width/sm": "--ani-border-width-sm",
    "target/target-size": "--ani-target-target-size",
  },
  semantic: {
    "content/default": "var(--ani-color-content-default)",
    "content/secondary": "var(--ani-color-content-secondary)",
    "content/muted": "var(--ani-color-content-muted)",
    "border/radius/minimal": "var(--ani-border-radius-sm)",
    "surface/default": "var(--ani-color-surface-default)",
    "surface/raised": "var(--ani-color-surface-raised)",
    "border/default": "var(--ani-color-border-default)",
    "action/default": "var(--ani-color-action-default)",
    "action/hover": "var(--ani-color-action-hover)",
    "action/pressed": "var(--ani-color-action-pressed)",
    "action/focus": "var(--ani-color-action-focus)",
    "action/disabled": "var(--ani-color-action-disabled)",
  },
  schema: {
    "Ani Button/Primary/--color": "var(--ani-button-primary-color)",
    "Ani Button/Primary/--color-hover": "var(--ani-button-primary-color-hover)",
    "Ani Button/Primary/--color-pressed": "var(--ani-button-primary-color-pressed)",
    "Ani Button/Primary/--text-color": "var(--ani-button-primary-text-color)",
    "Ani Button/--border-radius": "var(--ani-button-border-radius)",
    "Ani Card/Read-only/--background": "var(--ani-card-readonly-background)",
    "Ani Card/--border-radius": "var(--ani-card-border-radius)",
    "Ani Calendar/New/Base/--background": "var(--ani-calendar-base-background)",
    "Ani Chart/Content/--grid-chart": "var(--ani-chart-grid)",
    "Ani Search AI/Trigger/--background": "var(--ani-search-background)",
    "Ani Tag/Status/--color-neutral": "var(--ani-tag-status-color-neutral)",
    "Ani Tag/Status/--text-color-neutral": "var(--ani-tag-status-text-color-neutral)",
    "Ani Tag/Removable/--background": "var(--ani-tag-removable-color)",
  },
} as const;
